import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import useUserDetails from "../../utils/hooks/user/useUserDetails";

const MFAAnnouncementBanner: React.FC = () => {
  const { role } = useUserCredentials();
  const { otpEnabled, totpSecret } = useUserDetails();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Hide the banner ONLY if the user has configured the Authenticator App (totpSecret is active)
    if (totpSecret) {
      setVisible(false);
      return;
    }

    const firstShown = localStorage.getItem("ctb_mfa_first_shown");
    const now = Date.now();

    if (!firstShown) {
      localStorage.setItem("ctb_mfa_first_shown", now.toString());
    } else {
      const parsedFirstShown = parseInt(firstShown, 10);
      // 3 months expiration limit: 90 days = 90 * 24 * 60 * 60 * 1000 = 7776000000 ms
      if (now - parsedFirstShown > 7776000000) {
        setVisible(false);
        return;
      }
    }

    const dismissTime = localStorage.getItem("ctb_mfa_dismiss_time");
    const dontShow = localStorage.getItem("ctb_mfa_dont_show");

    if (dontShow === "true") {
      setVisible(false);
      return;
    }

    if (dismissTime) {
      const parsedTime = parseInt(dismissTime, 10);
      // Remind later: Hide for 24 hours (86400000 ms)
      if (now - parsedTime < 86400000) {
        setVisible(false);
        return;
      }
    }

    setVisible(true);
  }, [otpEnabled, totpSecret]);

  if (!visible) return null;

  // Determine security settings path based on user role
  const getSecurityLink = () => {
    if (role === UserRole.RESEARCHER) {
      return "/dashboard/profile/edit?tab=security"; // Researcher profile settings route contains security tab
    }
    return "/dashboard/settings/security"; // Administrative and business users settings page contains security tab
  };

  const handleRemindLater = () => {
    localStorage.setItem("ctb_mfa_dismiss_time", Date.now().toString());
    setVisible(false);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem("ctb_mfa_dont_show", "true");
    setVisible(false);
  };

  return (
    <div className="mx-4 mt-4 rounded-lg border border-[#010D2C]/20 bg-gradient-to-r from-[#010D2C]/5 to-indigo-50 shadow-md">
      <div className="px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#010D2C]/10 text-[#010D2C] shrink-0 mt-0.5">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="10" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Two-Factor Authentication (2FA)
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              TOTP-based authentication is now available. Set up an Authenticator App for stronger, offline 2FA protection.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <Link
            to={getSecurityLink()}
            className="px-3 py-1.5 bg-[#010D2C] hover:bg-[#010D2C]/90 text-white rounded-lg text-xs font-semibold shadow transition"
          >
            Set Up Now
          </Link>
          <button
            onClick={handleRemindLater}
            className="px-3 py-1.5 border border-gray-300 text-gray-700 bg-white rounded-lg text-xs font-semibold hover:bg-gray-50 transition"
          >
            Remind Me Later
          </button>
          <button
            onClick={handleDontShowAgain}
            className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 font-medium transition"
          >
            Don't show again
          </button>
        </div>
      </div>
    </div>
  );
};

export default MFAAnnouncementBanner;
